import styled from 'styled-components';

import { COLORS } from '@/shared/constants/colors';

const tokens = {
  radius: '8px',
  height: '56px',
  padX: '16px 16px',
  errorBlockHeight: '12px',
  // colors
  text: COLORS.colorFieldText,
  danger: COLORS.colorFiledDanger,
  error: COLORS.colorFieldErrorBg,
  default: COLORS.colorFieldBg,
  label: COLORS.colorFieldLabel,
  focus: COLORS.colorFieldHoverBg,
  borderColor: COLORS.colorFieldBorder,
  separation: COLORS.colorFieldOptionDivider,
  chipBg: COLORS.colorFieldChipBg,
  listBg: COLORS.colorFieldListBg,
};

export const FieldSelectView = styled.div`
  display: flex;
  align-items: center;
  height: ${tokens.height};
  padding-inline: ${tokens.padX};
  cursor: pointer;
  border-radius: ${tokens.radius};
  color: ${tokens.text};
  font-size: 14px;
  background: ${tokens.default};
  border: 1px solid ${tokens.default};
  position: relative;

  &.required::before {
    position: absolute;
    top: 0;
    left: 0;
    content: '';
    display: block;
    width: ${tokens.radius};
    height: ${tokens.radius};
    border-radius: 50%;
    background: ${tokens.danger};
  }

  &:focus {
    border: 1px solid ${tokens.borderColor};
  }

  &:hover {
    background: ${tokens.focus};
  }

  &.disabled {
    pointer-events: none;
    opacity: 0.5;
  }

  &.error {
    background: ${tokens.error};
  }

  &.error:hover {
    filter: brightness(95%);
  }

  .label {
    cursor: inherit;
    position: absolute;
    top: 16px;
    transition: transform var(--ani-speed) ease-in-out;
    color: ${tokens.label};
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &.selected .label {
    transform: translateY(-8px);
  }

  .labels {
    position: relative;
    flex-grow: 1;
    height: 100%;
    overflow: hidden;
    max-width: 100%;
  }

  .input-transparent {
    background: transparent;
    border: none;
    outline: none;
    caret-color: #000;
    color: ${tokens.text};
    font-size: 14px;
    min-width: 60px;
    width: 100%;
    text-overflow: ellipsis;
  }

  .selected-chips {
    // multiple select class
    position: absolute;
    color: ${tokens.text};
    left: 0;
    right: 0;
    bottom: 5px;
    overflow-x: auto;
    overflow-y: hidden;
    white-space: nowrap;
    scroll-behavior: smooth;
    scrollbar-width: none;
  }

  .selected-label {
    // single select class
    position: absolute;
    bottom: 7px;
    color: ${tokens.text};
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .selected-chips::-webkit-scrollbar {
    display: none;
  }

  .chip {
    // multiple select class
    background: ${tokens.chipBg};
    padding-inline: 4px 4px;
    border-radius: 4px;
    flex: 0 0 fit-content;
    margin-right: 4px;
  }

  .icons {
    display: flex;
    align-items: center;
    gap: 8px;
    padding-left: 6px;
  }

  .caret-down {
    transition: transform var(--ani-speed) ease-in-out;
  }

  &.open .caret-down {
    transform: rotate(180deg);
  }
`;

export const MainWrapper = styled.div`
  height: ${tokens.height};
  position: relative;

  .select-wrapper {
    min-width: fit-content;
    position: relative;
    border-radius: ${tokens.radius};
  }

  .oouk-select-visually-hidden {
    position: absolute;
    inset: 50% 50%;
    width: 1px;
    height: 1px;
    opacity: 0;
    pointer-events: none;
  }

  .error-text {
    top: ${tokens.height};
    position: absolute;
    font-size: 10px;
    height: ${tokens.errorBlockHeight};
    padding-left: 16px;
    color: ${tokens.danger};
  }
`;

export const List = styled.ul`
  background: ${tokens.listBg};
  position: absolute;
  z-index: 10;
  list-style: none;
  width: 100%;
  overflow: auto;
  max-height: 264px;
  margin-top: 4px;
  padding: 8px 0;
  border-radius: ${tokens.radius};
  box-shadow:
    0px 9px 28px 8px rgba(0, 0, 0, 0.05),
    0px 6px 6px -4px rgba(0, 0, 0, 0.12),
    0px 8px 16px 0px rgba(0, 0, 0, 0.08);

  & li ~ li {
    border-top: 1px solid ${tokens.separation};
  }
`;

export const OptionItemSingle = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 16px;
  border: 1px;
  cursor: pointer;
  font-size: 14px;

  .option-content {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-height: 20px;
    align-items: start;
  }

  .option-label {
    color: ${tokens.text};
  }

  .option-description {
    color: ${tokens.label};
  }

  .check {
    margin-right: 5px;
  }
`;

export const OptionItemMultiple = styled.li`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: start;
  padding: 6px 16px;
  border: 1px;
  cursor: pointer;
  font-size: 14px;

  .option-content {
    display: flex;
    flex: 1;
    flex-direction: column;
  }

  .option-label {
    line-height: 20px;
    color: ${tokens.text};
  }

  .option-description {
    line-height: 16px;
    font-size: 12px;
    color: ${tokens.label};
  }

  .checkbox {
    margin-top: 4px;
  }

  .checkbox.active:before {
    content: '';
    position: absolute;
    width: 18px;
    height: 18px;
    transform: translateX(-2.5px);
    border: 1.5px solid ${tokens.borderColor};
    border-radius: 6px;
  }
`;

export const EmptyCase = styled.li`
  padding: 10px 16px;
  font-size: 14px;
  color: ${tokens.label};
  background: ${tokens.default};
`;
